/**
 * Images in the interface (part 16b, tables I and A11y): the toolbar's Insert image with its file picker, and the
 * images' status under the toolbar, as the PDF's and the bundle's are: shown while images are put in, and afterwards only
 * when one didn't go in, with each file and why. What it has to say is announced once (Y3).
 */
import type { ImageRefusal } from '@cockatrice/core';
import type { InsertResult, InsertState } from '../images/insert.ts';
import { el, when } from '../ui/dom.ts';
import type { Child, On } from '../ui/dom.ts';
import type { FormatTool } from '../ui/FormatToolbar.ts';
import type { Shell } from './shell.ts';

/** I1: the files the picker offers (part 16a takes JPEG, PNG and WebP). */
export const IMAGE_TYPES = 'image/jpeg,image/png,image/webp';

/** I7: why the pipeline refused a file (part 16a, table L). */
function refusalText(shell: Shell, name: string, reason: ImageRefusal): string {
  const { t } = shell;
  switch (reason) {
    case 'unsupported':
      return t('images.unsupported', { name });
    case 'damaged':
      return t('images.damaged', { name });
    case 'too-large':
      return t('images.tooLarge', { name });
    case 'too-big':
      return t('images.tooBig', { name });
  }
}

/** I7, I10: why one file didn't go in, or null when it did. */
export function resultText(shell: Shell, result: InsertResult): string | null {
  const { t } = shell;
  const { name, outcome } = result;
  switch (outcome.kind) {
    case 'put-in':
      return null;
    case 'refused':
      return refusalText(shell, name, outcome.reason);
    case 'full':
      return t('images.full', { name });
    case 'failed':
      return t('images.failed', { name });
    case 'not-put-in':
      return t('images.notPutIn', { name });
  }
}

/** The lines of the files that didn't go in. */
function problems(shell: Shell, state: InsertState): string[] {
  return state.kind === 'finished' ? state.results.flatMap((result) => resultText(shell, result) ?? []) : [];
}

/** What the status says first: the file under way, or how many went in, or that not every one did. */
export function imagesText(shell: Shell, state: InsertState): string {
  const { t } = shell;
  switch (state.kind) {
    case 'idle':
      return '';
    case 'working':
      return state.total === 1 ? t('images.working', { name: state.name }) : t('images.workingOf', { number: state.done + 1, total: state.total, name: state.name });
    case 'finished':
      return problems(shell, state).length > 0 ? t('images.notAll') : t('images.putIn', { count: state.results.length });
  }
}

/** Y3: what the images announce: the status, with each file that didn't go in. */
export function imagesNews(shell: Shell, state: InsertState): string {
  return [imagesText(shell, state), ...problems(shell, state)].join(' ');
}

/** Whether the status shows: while images go in, and after only when one didn't (Y3: success is said, and seen in the text). */
function showing(shell: Shell, state: InsertState): boolean {
  return state.kind === 'working' || problems(shell, state).length > 0;
}

/**
 * Y1, Y2, I1, I12: the formatting bar's Insert image (the library's FormatToolbar tool), with its own file picker
 * (hidden, beside the bar; the tool opens it). Off with no chapter open or while the books can't change.
 */
export function InsertImage(shell: Shell): { readonly tool: FormatTool; readonly picker: Child; readonly open: () => void } {
  const { t, workspace } = shell;
  let picker: HTMLInputElement | undefined;
  return {
    tool: {
      id: 'picture',
      icon: 'picture',
      toggle: false,
      class: 'insert-image',
      get label() {
        return t('toolbar.insertImage');
      },
      get disabled() {
        return workspace.editor() === null || !workspace.writable();
      },
    },
    open: () => picker?.click(),
    picker: el('input', {
      type: 'file',
      class: 'image-picker',
      accept: IMAGE_TYPES,
      multiple: true,
      hidden: true,
      tabIndex: -1,
      'aria-hidden': 'true',
      ref: (input: HTMLInputElement) => {
        picker = input;
      },
      onChange: (event: On<Event, HTMLInputElement>) => {
        const files = Array.from(event.currentTarget.files ?? []);
        // The same file chosen again must still fire a change.
        event.currentTarget.value = '';
        shell.images.insert(files);
        shell.focusEditor();
      },
    }),
  };
}

/** Y3: the status region; its Dismiss hands the focus to the text (the next step), as the job is over. */
export function ImagesStatus(shell: Shell): Child {
  const { t, images } = shell;
  const state = (): InsertState => images.state();
  return when(
    () => showing(shell, state()),
    () =>
      el(
        'section',
        { class: 'export-status images-status', 'aria-label': t('images.label') },
        el('p', {}, () => imagesText(shell, state())),
        () => problems(shell, state()).map((line) => el('p', { class: 'image-problem' }, line)),
        () => {
          const now = state();
          return now.kind === 'working' ? el('progress', { max: now.total, value: now.done }) : null;
        },
        when(
          () => state().kind === 'finished',
          () =>
            el(
              'button',
              {
                type: 'button',
                class: 'dismiss-images',
                onClick: () => {
                  images.dismiss();
                  shell.focusEditor();
                },
              },
              t('export.dismiss'),
            ),
        ),
      ),
  );
}
